import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import lodashCompact from 'lodash/fp/compact';

import {
  SR_API_ACTION_UTILITY_VERIFY_PHONE_SMS_CODE_SUBMIT,
  SR_API_ACTION_UTILITY_GENERATE_RESET_PASSWORD_TOKEN,
} from 'api/utilityActionTypes';
import { UTILITY_USER_ROLE_ANONYMOUS } from 'common/utility/userRoles';
import { SCREEN_ID_UTILITY_RESET_PASSWORD_VALIDATE_CODE } from 'client/constants/screenIds';

import log from 'client/lib/log';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import withApiState from 'client/helpers/withApiState';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import branch from 'client/core/branch';
import UtilityAuthActions from 'client/actions/UtilityAuth';
import UtilityPartnerActions from 'client/actions/UtilityPartner';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';
import SrPasswordResetSmsVerifyCode from 'client/components/SrPasswordResetSmsVerifyCode';
import SrSmsSendingIndicator from 'client/components/SrSmsSendingIndicator';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ANONYMOUS],
  redirectUrl: '/utility-setup/session',
})
@withApiState({
  verifyPhoneApiState: {
    action: SR_API_ACTION_UTILITY_VERIFY_PHONE_SMS_CODE_SUBMIT,
  },
})
@branch(({ match }) => {
  const cursors = {
    phone: ['resetPasswordForm', 'phone'],
  };

  if (match.params.partnerSlug) {
    cursors.utilityPartner = ['utilityPartners', 'bySlug', match.params.partnerSlug];
  }

  return cursors;
})
@withScreenId(SCREEN_ID_UTILITY_RESET_PASSWORD_VALIDATE_CODE)
@withScrollToTop
class UtilityResetPasswordVerifyCodePage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Reset Password',
    section: SCREEN_ID_UTILITY_RESET_PASSWORD_VALIDATE_CODE,
  });

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    phone: PropTypes.string.isRequired,
    verifyPhoneApiState: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    return Promise.all(
      lodashCompact([
        match.params.partnerSlug &&
          UtilityPartnerActions.getBySlug(inject, {
            slug: match.params.partnerSlug,
          }),
      ]),
    );
  }

  constructor(props) {
    super(props);
    this.state = {
      showSmsSending: false,
      incorrectCodeSubmitted: false,
    };
    this._unmounting = false;
    this._onRequestVerificationCode = this._onRequestVerificationCode.bind(this);
  }

  async _validateVerificationCode(phone, code) {
    const { dispatch, history, location } = this.props;

    const urlParts = location.pathname.split('/');
    urlParts.pop();

    try {
      const token = await dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_GENERATE_RESET_PASSWORD_TOKEN,
        payload: { phone, code, appName: urlParts[1] },
      });

      if (!token) {
        throw new Error('Invalid code');
      }

      let url = `${urlParts.join('/')}/reset-password-confirm?`;
      url += queryString.stringify({
        ...queryString.parse(location.search || ''),
        token,
      });
      history.push(url);
    } catch (error) {
      this.setState({ incorrectCodeSubmitted: true });
      log.error(error);
    }
  }

  _onRequestVerificationCode(phone) {
    const { dispatch, location } = this.props;

    const urlParts = location.pathname.split('/');
    urlParts.pop();

    this.setState({
      showSmsSending: true,
    });

    setTimeout(() => {
      if (!this._unmounting) {
        this.setState({
          showSmsSending: false,
        });
      }
    }, 3000);

    dispatch(UtilityAuthActions.resetPasswordSmsCodeRequested, {
      phone,
      isUtilityProfit: urlParts[1] === 'utility-profit',
    });
  }

  render() {
    const { verifyPhoneApiState, phone, utilityPartner } = this.props;
    const { showSmsSending, incorrectCodeSubmitted } = this.state;

    const urlParts = location.pathname.split('/');
    urlParts.pop();

    const isUtilityProfit = urlParts[1] === 'utility-profit';

    return (
      <div className="page-reset-password">
        <Navbar
          disableRedirect
          customLogo={
            <SrFlex justify="center">
              <SrBox
                backgroundSize="contain"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                height="40px"
                width="100%"
                maxWidth="166px"
                backgroundImage={`url(${
                  isUtilityProfit ? '/images/utility-profit-logo.svg' : utilityPartner?.logoUrl
                })`}
              />
            </SrFlex>
          }
          leftButton={
            <SrLink
              className="link back"
              redirectToLastLocation
              restoreScrollPosition
              to={urlParts.join('/')}
            >
              Back
            </SrLink>
          }
        />
        <div className="resetContainer">
          <div className="resetFormContainer">
            {incorrectCodeSubmitted && (
              <div className="formMessage error">
                <span>Uh oh, that's not the right code!</span>
              </div>
            )}
            {!verifyPhoneApiState.inProgress && !showSmsSending && (
              <SrPasswordResetSmsVerifyCode
                onSubmit={({ code }) => this._validateVerificationCode(phone, code)}
              />
            )}
            {showSmsSending && <SrSmsSendingIndicator phone={phone} />}
            {!showSmsSending && (
              <div className="action-link-container">
                <a onClick={() => this._onRequestVerificationCode(phone)}>
                  Didn't get the code? Click to send another.
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

UtilityResetPasswordVerifyCodePage.displayName = 'UtilityResetPasswordVerifyCodePage';

export default UtilityResetPasswordVerifyCodePage;
